import { useCallback } from 'react'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import type { NavigationEntry } from '@/modules/tasks/components/task-navigation'
import { useDeleteRepeatableTask } from '@/modules/tasks/query/repeatable-task-query.ts'
import { useDeleteTask } from '@/modules/tasks/query/task-query'
import { handleError } from '@/utils/error'

interface DeleteTaskDialog {
  task: NavigationEntry | undefined
  show: boolean
  onVisibilityChange: (state: boolean) => void
}

export const DeleteTaskDialog = (props: DeleteTaskDialog) => {
  const { task, show, onVisibilityChange } = props

  const hideDialog = useCallback(
    () => onVisibilityChange(false),
    [onVisibilityChange],
  )

  const deleteTask = useDeleteTask()
  const deleteRepeatableTask = useDeleteRepeatableTask()

  const toast = useToast()

  const handleDelete = useCallback(() => {
    if (!task) {
      return
    }
    const deleteFunction =
      task.type === 'task'
        ? deleteTask.mutateAsync
        : deleteRepeatableTask.mutateAsync

    deleteFunction(task.id)
      .then(() => {
        toast.success('Task deleted')
        hideDialog()
      })
      .catch((error) => {
        handleError(error, 'Error deleting task')
      })
  }, [
    deleteRepeatableTask.mutateAsync,
    deleteTask.mutateAsync,
    hideDialog,
    task,
    toast,
  ])

  return (
    <Dialog show={show}>
      <Dialog.Header>
        <Dialog.Title>Are you sure?</Dialog.Title>
        <Dialog.Description>
          This action cannot be undone. This will permanently remove the task
          &apos;{task?.name}&apos;
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.ClosableFooter
        className="mt-6"
        onClose={hideDialog}
      >
        <Button
          variant="destructive"
          onClick={handleDelete}
          isLoading={deleteTask.isLoading}
        >
          Remove
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
